import { CriteriaListItem, CriteriaTypeEnum } from 'src/app/modules/SimulationTool/types/criteria-setting';
import { ParameterItem } from 'src/app/modules/SimulationTool/types/parameter-setting';
import { makeStyles } from 'tss-react/mui';
import { parameterFormReducer, ParameterFormState } from '../reducers/form-reducer';
import { FC, useMemo, useReducer } from 'react';
import { useCommonStyles } from 'src/app/common/styles/common-styles';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { AwardFormState } from '../reducers/award-form-reducer';
import AwardSection from './AwardSection';
import { ErrorFieldType, numberSeparator, removeSeparator, useErrorHandler } from 'src/app/common/utils';
import { Button } from '@mui/material';
import { useHistory } from 'react-router-dom';
import { PARAMETER_PATH, SIMULATION_TOOL_PATH } from 'src/app/modules/SimulationTool/constants';
import { UpdateParameterBody, updateParameterItem } from 'src/app/modules/SimulationTool/network/parameterCrud';
import { AlertType, appendAlertItem } from '@pruforce/common-adminweb-sdk';

type ParameterDetailFormProps = {
  id?: string;
  criteriaList: CriteriaListItem[];
  parameterDetail?: ParameterItem;
  onRouteTo: (route: string) => void;
};

const detailToStateConvertor = (detail?: ParameterItem): ParameterFormState => {
  if (detail) {
    return {
      code: detail.code,
      name: detail.name,
      type: detail.type,
      awards: detail.awards
        ? detail.awards.map((tier) => ({
            ...tier,
            requirements: tier?.requirements?.map((requirement) => ({
              ...requirement,
              items: requirement?.items?.map((item) => ({
                ...item,
                value:
                  detail.type === CriteriaTypeEnum.NUMBER
                    ? String(numberSeparator(String(item.value))) || ''
                    : String(item.value),
              })),
            })),
          }))
        : [],
    };
  } else {
    return {
      code: '',
      name: '',
      type: '',
      awards: [],
    };
  }
};
const FIELD_CONTAINER_WIDTH = 160;

const useStyles = makeStyles()((theme) => ({
  container: {
    padding: 20,
    marginBottom: 20,
    borderRadius: 5,
    backgroundColor: theme.palette.common.white,
  },

  headerContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 18,
  },
  section: {
    padding: 32,
    marginBottom: 32,
    borderRadius: 8,
    backgroundColor: theme.palette.common.white,
  },
  rowContainer: {
    display: 'flex',
    alignItems: 'center',
  },
  rowSpacing: {
    marginBottom: 16,
  },
  colSpacing: {
    marginRight: 16,
  },
  multiRowContainer: {
    display: 'flex',
  },
  fieldContainer: {
    minWidth: FIELD_CONTAINER_WIDTH,
    boxSizing: 'border-box',
  },
  field: {
    fontSize: '1rem',
    marginRight: 10,
  },
}));

const ParameterSettingForm: FC<ParameterDetailFormProps> = ({ id, criteriaList, parameterDetail, onRouteTo }) => {
  const { classes } = useStyles();
  const { classes: commonClasses } = useCommonStyles();
  const intl = useIntl();
  const Translation = (id: string, variable?: Record<string, string>) => intl.formatMessage({ id }, variable);
  const dispatch = useDispatch();
  const history = useHistory();

  const [formState, formDispatch] = useReducer(parameterFormReducer, detailToStateConvertor(parameterDetail));
  const { errorState, onSubmitErrorValidator, onDismissErrorHandler, immediateErrorValidator } = useErrorHandler(
    formState,
    [
      {
        name: 'awards',
        fieldType: ErrorFieldType.MANDATORY,
        condition: () => {
          if (formState.awards.length !== 0) {
            for (const award of formState.awards) {
              for (const requirement of award.requirements) {
                for (const item of requirement.items) {
                  if (!!!item.keyId || !!!item.value || item.value.length === 0) {
                    return true;
                  }
                }
              }
            }
          }
          return false;
        },
      },
    ],
  );

  const onSubmit = async () => {
    const { hasError } = onSubmitErrorValidator();
    if (!hasError) {
      const body: UpdateParameterBody = {
        awards: formState.awards
          ? formState.awards.map((tier) => ({
              ...tier,
              requirements: tier?.requirements?.map((requirement) => ({
                ...requirement,
                items: requirement?.items?.map((item) => ({
                  ...item,
                  value:
                    parameterDetail?.type === CriteriaTypeEnum.NUMBER
                      ? String(Number(removeSeparator(item.value || '')))
                      : String(Number(item.value)),
                })),
              })),
            }))
          : [],
      };

      try {
        if (parameterDetail) {
          await updateParameterItem(parameterDetail._id, body, dispatch);
          dispatch(
            appendAlertItem([
              {
                severity: AlertType.SUCCESS,
                title: 'Success',
                content: `Parameter updated successfully`,
              },
            ]),
          );
        }
        history.push(`${SIMULATION_TOOL_PATH}${PARAMETER_PATH}`);
      } catch (err) {}
    }
  };

  const awardSection = useMemo(() => {
    const initialAwardFormState: AwardFormState = {
      awards: formState.awards,
      editableTiers: formState.awards.map((award) => ({
        editable: false,
        tierCode: award.tierCode,
        name: award.name,
        error: false,
      })),
    };
    if (criteriaList.length !== 0) {
      return (
        <AwardSection
          isDisabled={false}
          criteriaList={criteriaList}
          initialFormState={initialAwardFormState}
          hasError={errorState.mandatory.awards}
          onChange={(newAwards) =>
            formDispatch({ type: 'MODIFY_FIELD', payload: { field: 'awards', value: newAwards } })
          }
          onDismissErrorHandler={onDismissErrorHandler}
          paremeterType={parameterDetail?.type || ''}
        />
      );
    }
  }, [parameterDetail, criteriaList, errorState.mandatory.awards]);

  return (
    <div className={classes.container}>
      <div className={classes.headerContainer}>
        <div className={classes.rowContainer}>
          <div className={commonClasses.header}>Parameter Setting</div>
        </div>
        <div>
          <div className={classes.multiRowContainer}>
            <Button className={classes.colSpacing} variant="contained" color="secondary" onClick={onSubmit}>
              {Translation('app.button.save')}
            </Button>

            <Button
              variant="contained"
              color="inherit"
              onClick={() => history.push(`${SIMULATION_TOOL_PATH}${PARAMETER_PATH}`)}
            >
              {Translation('app.button.back')}
            </Button>
          </div>
        </div>
      </div>

      <div className={classes.section}>
        <div className={`${classes.rowContainer} ${classes.rowSpacing}`}>
          <div className={classes.fieldContainer}>
            <span className={classes.field}>{Translation(`parameterSetting.code`)} :</span>
          </div>
          <div style={{ flexGrow: 1 }}>{parameterDetail?.code}</div>
        </div>

        <div className={`${classes.rowContainer} ${classes.rowSpacing}`}>
          <div className={classes.fieldContainer}>
            <span className={classes.field}>{Translation(`parameterSetting.type`)} :</span>
          </div>
          <div style={{ flexGrow: 1 }}>{parameterDetail?.type}</div>
        </div>

        <div className={`${classes.rowContainer} ${classes.rowSpacing}`}>
          <div className={classes.fieldContainer}>
            <span className={classes.field}>{Translation(`parameterSetting.name`)} :</span>
          </div>
          <div style={{ flexGrow: 1 }}>{parameterDetail?.name}</div>
        </div>

        {/* Award Settings */}
        <div className={classes.multiRowContainer}>
          <div className={classes.fieldContainer}>
            <span className={classes.field}>{Translation('parameterSetting.tier_setting')} :</span>
          </div>
          <div style={{ flexGrow: 1, maxWidth: `calc(100% - ${FIELD_CONTAINER_WIDTH}px)` }}>{awardSection}</div>
        </div>
      </div>
    </div>
  );
};

export default ParameterSettingForm;
